import { formatAmount, formatDate } from '@beta-assur/rules';
import { toMillimes, type IsoDate } from '@beta-assur/shared';
import type { PortalLanguage } from './i18n';

// Montants en millimes entiers, dates calendaires et instants affichés dans le fuseau du tenant.

export const DEFAULT_TIMEZONE = 'Africa/Tunis';

/** Montant reçu de l'API (millimes entiers), validé avant formatage. */
/** Anglais : mêmes conventions que le français pour les montants (DT, trois décimales). */
const ruleLanguage = (language: PortalLanguage) => (language === 'ar' ? 'ar' : 'fr');

export function money(millimes: number, language: PortalLanguage = 'fr'): string {
  const value = toMillimes(millimes);
  if (language !== 'en') return formatAmount(value, ruleLanguage(language));
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const units = Math.floor(abs / 1000).toLocaleString('en-US');
  return `${sign}${units}.${String(abs % 1000).padStart(3, '0')} DT`;
}

export function day(date: string | null | undefined, language: PortalLanguage = 'fr'): string {
  if (!date) return '';
  const iso = date.slice(0, 10) as IsoDate;
  if (language !== 'en') return formatDate(iso, ruleLanguage(language));
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const LOCALES: Record<PortalLanguage, string> = { fr: 'fr-TN', en: 'en-GB', ar: 'ar-TN' };

export function instant(
  iso: string | null | undefined,
  timezone = DEFAULT_TIMEZONE,
  language: PortalLanguage = 'fr',
): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(LOCALES[language], {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}

/**
 * Saisie en dinars (« 1 250,5 », « 1250.500 ») vers millimes entiers, sans passer par un
 * flottant : partie entière et trois décimales au plus. `null` si la saisie est invalide.
 */
export function parseDinars(input: string): number | null {
  const compact = input.replace(/\s/g, '').replace(/(DT|TND)$/i, '');
  const match = /^(\d+)(?:[.,](\d{1,3}))?$/.exec(compact);
  if (!match) return null;
  const millimes = (match[2] ?? '').padEnd(3, '0');
  const value = Number(match[1]) * 1000 + Number(millimes);
  return Number.isSafeInteger(value) ? value : null;
}

/** Millimes vers la saisie en dinars (« 1250,500 »). */
export function toDinarsInput(millimes: number): string {
  return `${Math.floor(millimes / 1000)},${String(millimes % 1000).padStart(3, '0')}`;
}
